import { Input } from '@/app/common/components/Input'
import { EyeIcon, EyeSlashIcon, LockIcon } from '@/app/common/icons/general'
import { ChangeEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'

export const MASKED_SSN_STRUCTURE = '***-**-'

export const isValidSSN = (ssn: string) => {
  const cleaned = ssn.replace(/-/g, '')

  if (cleaned.length !== 9) return false
  if (/^0+$/.test(cleaned)) return false
  if (/^000/.test(cleaned.substring(0, 3))) return false
  if (/^00/.test(cleaned.substring(3, 5))) return false
  if (/^0000/.test(cleaned.substring(6, 9))) return false
  if (/^666|^9[0-9]{2}$/.test(cleaned.substring(0, 3))) return false
  if (/^0{2}/.test(cleaned.substring(3, 5))) return false
  if (/^0{4}/.test(cleaned.substring(5))) return false
  return true
}

const formatSSN = (ssn: string) => {
  let val = ssn.replace(/[^\d-]/g, '')

  val = val.replace(/^(\d{3})-?(\d{1,2})/, '$1-$2')
  val = val.replace(/^(\d{3})-?(\d{2})-?(\d{1,4})/, '$1-$2-$3')

  val = val
    .split('')
    .filter((val, idx) => {
      return val !== '-' || idx === 3 || idx === 6
    })
    .join('')
  return val.substring(0, 11)
}

export const isMaskedSSN = (social_security_number: string) => {
  return social_security_number.includes(MASKED_SSN_STRUCTURE)
}

interface SSNInputProps {
  onChange: (value: string) => void
  error?: string
  value: string
  label?: string
  optional?: boolean
  shouldDisplayEncryptedMessage?: boolean
}

export default function SSNInput({
  value,
  onChange,
  error,
  label = 'Social Security Number',
  optional = false,
  shouldDisplayEncryptedMessage,
}: SSNInputProps) {
  const { t: translate } = useTranslation()
  const [isMasked, setIsMasked] = useState(true)

  const handleSSNChange = (e: ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatSSN(e.target.value)
    onChange(formattedValue)
  }

  return (
    <div className="flex flex-col space-y-2">
      <label
        htmlFor="social_security_number"
        className="flex justify-between text-sm font-medium leading-6 text-gray-900"
      >
        <span>{label}</span>
        {optional && (
          <span className="text-sm font-small text-gray-500 font-normal">
            {translate('optional')}
          </span>
        )}
      </label>
      <div className="relative mt-2 rounded-md shadow-sm">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <span className="text-primary-500 sm:text-sm">
            <LockIcon />
          </span>
        </div>
        <Input
          className="block w-full pl-10 pr-20"
          maxLength={11}
          autoComplete="off"
          readOnly={true}
          value={value}
          onFocus={(event) => {
            event.target.readOnly = false
            const ssn = value || ''
            if (isMaskedSSN(ssn)) {
              setIsMasked(true)
              onChange('')
            }
          }}
          onChange={handleSSNChange}
          placeholder="XXX-XX-XXXX"
          inputMode="numeric"
          type={isMasked ? 'password' : 'text'}
        />
        <div
          className="absolute right-0 top-0 flex items-center h-full mr-2 cursor-pointer"
          onClick={() => setIsMasked(!isMasked)}
        >
          {isMasked ? <EyeIcon /> : <EyeSlashIcon />}
        </div>
      </div>
      {error && <div className="text-red-500 text-sm">{translate(error)}</div>}
      {shouldDisplayEncryptedMessage && (
        <div className="text-sm font-small text-gray-500 mb-3 sm:mb-2">
          {translate('verify_code_is_encrypted')}
        </div>
      )}
    </div>
  )
}
